import { type FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form } from 'react-bootstrap';

import ModalWindow from '@editor2/components/ModalWindow';
import { addElementToPage, editElementsCollapseState } from '@editor2/reducers/editor/editorSlice';
import { type RootState } from '@editor2/reducers/root';
import { getNewElement, isDoubleElement } from '@editor2/services/elementService';
import { type PageElement, ElementConfigName, ElementType } from '@editor2/types/pageElements';
import { DEPRECATED_ELEMENTS } from '@editor2/types/deprecatedElements';
import { CollapseState } from '@editor2/types/collapseState';
import { ALLOWED_IN_QUESTION_DECK } from '@editor2/constants/allowedInQuestionDeck';
import { ALLOWED_NESTING } from '@editor2/types/allowedNesting';
import { findNearestDoubleElement } from '@editor2/services/doubleElementValidationsService';
import { getEnabledPageElementsTypes } from '@editor2/services/pagesService';
import { useAiModels } from '@editor2/hooks/useAiModels';
import AiModelsStatus from '@editor2/components/AiModelsStatus';

interface Props {
	pageId: string;
	index?: number;
	isShown: boolean;
	courseId: number;
	onHide: () => void;
}

const AddElementModal: FC<Props> = (props) => {
	const { pageId, index, isShown, courseId, onHide } = props;

	const dispatch = useDispatch();
	const { pages, elements } = useSelector((state: RootState) => state.courses[courseId]);
	const { page_types, element_types } = useSelector(
		(state: RootState) => state.courses[courseId].config
	);

	const pageType = pages.entities[pageId].page_type;
	const elementTypes = getEnabledPageElementsTypes(page_types, pageType);

	const elementId = pages.entities[pageId].elements[index - 1];
	const element = elements && elements.entities[elementId];

	const [elementTypesKeys, setElementTypesKeys] = useState([]);
	const [elementType, setElementType] = useState(null);
	const isChatbox = (element_types[elementType]?.type || elementType) === 'AiChatbox';
	const catalog = useAiModels(isShown && isChatbox);
	const hasDefaults = catalog.models?.some((model) => model.default);
	const chatboxBlocked = isChatbox && (!hasDefaults || catalog.error || catalog.loading);

	const addElementSubmit = () => {
		if (chatboxBlocked) return;
		const elementConfig = element_types[elementType];
		const elementEntity = getNewElement(elementType, elementConfig, catalog.models);

		dispatch(addElementToPage({ pageId, elementEntity, index, courseId }));
		dispatch(editElementsCollapseState({ collapseState: CollapseState.Mixed, courseId }));

		if (isDoubleElement(ElementType[elementType])) {
			const elementEntity = getNewElement(elementType, elementConfig);
			const payload = {
				pageId,
				elementEntity: { ...elementEntity, direction: 'out' },
				index: index + 1,
				courseId
			};

			dispatch(addElementToPage(payload));
		}

		onHide();
	};

	const title = <div>Add new element</div>;

	const footer = (
		<Button variant="success" size="sm" onClick={addElementSubmit} disabled={chatboxBlocked}>
			Add
		</Button>
	);

	const getAllowedInQuestionDeckTypes = (elementInside: PageElement) => {
		// if elementInside is QuestionDeck that means the QuestionDeck is empty
		// and we can add any element from ALLOWED_IN_QUESTION_DECK
		// otherwise we limit allowed types to the one that QuestionDeck already has

		switch (elementInside.type) {
			case ElementType.QuestionDeck:
				const elementTypesKeys = mapElementTypesKeys(ALLOWED_IN_QUESTION_DECK);
				// filter enabled for the current page types
				return elementTypesKeys.filter((type) => elementTypes[type]?.enabled);

			case ElementType.MCQuestion:
			case ElementType.MCQuestionPool:
				return [ElementConfigName[elementInside.type]];

			// Poll and Poll Alias are allowed together in QuestionDeck
			case ElementType.Poll:
			case ElementType.PollResults:
				return [ElementConfigName[ElementType.Poll], ElementConfigName[ElementType.PollResults]];

			default:
				break;
		}
	};

	const getAllowedInsideDoubleTypeKeys = () => {
		const pageElements = pages.entities[pageId].elements;
		const nearestDoubleElement = findNearestDoubleElement(pageElements, index, elements);

		switch (nearestDoubleElement.type) {
			case ElementType.NestingInstruction:
				return mapElementTypesKeys(ALLOWED_NESTING);

			case ElementType.QuestionDeck:
				const nearestDoubleOutElementIndex = pages.entities[pageId].elements.indexOf(
					nearestDoubleElement.family_id
				);
				const prevElementId = pages.entities[pageId].elements[nearestDoubleOutElementIndex - 1];
				const prevElement = elements?.entities[prevElementId];

				return getAllowedInQuestionDeckTypes(prevElement);

			default:
				break;
		}

		return elementTypesKeys;
	};

	const mapElementTypesKeys = (elementsTypes) => {
		return elementsTypes.map((type) => ElementConfigName[type]);
	};

	useEffect(() => {
		let allowedTypesKeys = Object.keys(elementTypes);

		switch (true) {
			case element.type === ElementType.QuestionDeck && element.direction === 'in':
				const nextElementId = pages.entities[pageId].elements[index];
				const nextElement = elements?.entities[nextElementId];

				allowedTypesKeys = getAllowedInQuestionDeckTypes(nextElement);
				break;

			case element.type === ElementType.NestingInstruction && element.direction === 'in':
				allowedTypesKeys = mapElementTypesKeys(ALLOWED_NESTING);
				break;

			case element.indent:
				allowedTypesKeys = getAllowedInsideDoubleTypeKeys();
				break;

			default:
				break;
		}

		setElementTypesKeys(allowedTypesKeys);
		setElementType(allowedTypesKeys[0]);
	}, []);

	return (
		<ModalWindow isShown={isShown} onHide={onHide} title={title} footer={footer}>
			{isChatbox && <AiModelsStatus {...catalog} noDefaults={!!catalog.models && !hasDefaults} />}
			<Form.Group>
				<Form.Label>Element type</Form.Label>
				<Form.Control as="select" onChange={(e) => setElementType(e.target.value)}>
					{elementTypesKeys.map(
						(type) =>
							!DEPRECATED_ELEMENTS.includes(ElementType[type]) && (
								<option key={type} value={type}>
									{elementTypes[type].human_name}
								</option>
							)
					)}
				</Form.Control>
			</Form.Group>
		</ModalWindow>
	);
};

export default AddElementModal;
